import { defineStore } from 'pinia'
import { ref } from 'vue'
import { BaseStore } from './baseStore'

interface PaymentStatus {
  id?: string
  organizationId: string
  name: string
  description?: string
  color?: string
  active?: boolean
  createdAt?: string
  updatedAt?: string
}

class PaymentStatusStore extends BaseStore {
  constructor() {
    super('paymentStatuses')
  }

  async fetchPaymentStatuses() {
    return await this.fetchCollection<PaymentStatus>()
  }

  async addPaymentStatus(status: PaymentStatus) {
    return await this.addDocument<PaymentStatus>(status)
  }

  async updatePaymentStatus(id: string, data: Partial<PaymentStatus>) {
    return await this.updateDocument<PaymentStatus>(id, data)
  }

  async deletePaymentStatus(id: string) {
    return await this.deleteDocument(id)
  }
}

export const usePaymentStatusStore = defineStore('paymentStatus', () => {
  const paymentStatusStore = new PaymentStatusStore()
  const paymentStatuses = ref<PaymentStatus[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const fetchPaymentStatuses = async () => {
    loading.value = true
    error.value = null
    try {
      paymentStatuses.value = await paymentStatusStore.fetchPaymentStatuses()
    } catch (err: any) {
      error.value = 'Erro ao carregar status de pagamento: ' + err.message
      console.error('Error fetching payment statuses:', err)
    } finally {
      loading.value = false
    }
  }

  const createPaymentStatus = async (status: PaymentStatus) => {
    loading.value = true
    error.value = null
    try {
      await paymentStatusStore.addPaymentStatus(status)
      await fetchPaymentStatuses()
    } catch (err: any) {
      error.value = 'Erro ao criar status de pagamento: ' + err.message
      console.error('Error creating payment status:', err)
    } finally {
      loading.value = false
    }
  }

  const updatePaymentStatus = async (id: string, status: Partial<PaymentStatus>) => {
    loading.value = true
    error.value = null
    try {
      await paymentStatusStore.updatePaymentStatus(id, status)
      await fetchPaymentStatuses()
    } catch (err: any) {
      error.value = 'Erro ao atualizar status de pagamento: ' + err.message
      console.error('Error updating payment status:', err)
    } finally {
      loading.value = false
    }
  }

  const deletePaymentStatus = async (id: string) => {
    loading.value = true
    error.value = null
    try {
      await paymentStatusStore.deletePaymentStatus(id)
      await fetchPaymentStatuses()
    } catch (err: any) {
      error.value = 'Erro ao excluir status de pagamento: ' + err.message
      console.error('Error deleting payment status:', err)
    } finally {
      loading.value = false
    }
  }

  return {
    paymentStatuses,
    loading,
    error,
    fetchPaymentStatuses,
    createPaymentStatus,
    updatePaymentStatus,
    deletePaymentStatus
  }
})
